/**
 * Mission Planning Slice
 *
 * Zustand slice for the mission creation planning flow (00b inference + 00c confirmation).
 * Domain: MissionContext > MissionPlanningAggregate
 *
 * GW-4907: Inference and confirmation pages for mission creation flow.
 * GW-5112: Auto-populate name from mission type and target.
 * GW-5113: Default classification to CONFIDENTIAL.
 * GW-5115: Auto-generate tags from mission content.
 */

import type { PhaseTokenEstimate } from '@/components/missions/workspace/TokenApprovalCard';

import type { MissionType } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

export interface IdentifiedEntity {
  name: string;
  type: 'person' | 'organization' | 'location' | 'group';
  confidence: number; // 0-1
  aliases: string[];
  jurisdiction?: string;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ============================================================================
// Mission Type Labels (GW-5112)
// ============================================================================

const MISSION_TYPE_LABELS: Record<MissionType, string> = {
  'M-02': 'Person of Interest',
  'M-06': 'Financial Investigation',
  'M-11': 'Due Diligence',
  'M-21': 'Threat Assessment',
  'M-25': 'Supply Chain',
  'M-27': 'Geopolitical Analysis',
};

export function getMissionTypeLabel(code: MissionType): string {
  return MISSION_TYPE_LABELS[code] ?? code;
}

// ============================================================================
// Mission Type Tag Slugs (GW-5115)
// ============================================================================

const MISSION_TYPE_TAG_SLUGS: Record<MissionType, string> = {
  'M-02': 'person-of-interest',
  'M-06': 'financial-investigation',
  'M-11': 'due-diligence',
  'M-21': 'threat-assessment',
  'M-25': 'supply-chain',
  'M-27': 'geopolitical-analysis',
};

// ============================================================================
// Types
// ============================================================================

export interface StreamLine {
  id: string;
  prompt: string;
  text: string;
  boldSegments?: string[];
  isComplete: boolean;
}

export interface InferredDimension {
  id: string;
  label: string;
  status: 'confirmed' | 'uncertain';
}

export interface MissionDataset {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: 'platform' | 'marketplace';
  relevanceScore: number;
  requiresCredentials: boolean;
  publisherName?: string;
  tokenCost?: number;
  pricePerQuery?: number;
  pricePerRecord?: number;
  isPromoted?: boolean;
  isPopular?: boolean;
  isTrending?: boolean;
}

type PlanningStatus = 'idle' | 'inferring' | 'confirming' | 'launching';
export type SetupCardId =
  | 'mission-type'
  | 'mission-config'
  | 'datasets'
  | 'credentials'
  | 'token-approval';

const CARD_ORDER: SetupCardId[] = [
  'mission-type',
  'mission-config',
  'datasets',
  'credentials',
  'token-approval',
];

// ============================================================================
// Slice Interface
// ============================================================================

export interface MissionPlanningSlice {
  pendingPrompt: string;
  pendingChipId: string | null;
  missionType: MissionType | null;
  planningStatus: PlanningStatus;
  streamLines: StreamLine[];
  inferredDimensions: InferredDimension[];
  inferenceProgress: number;
  activeCard: SetupCardId | null;
  confirmedCards: SetupCardId[];
  availableDatasets: MissionDataset[];
  selectedDatasetIds: string[];
  identifiedEntities: IdentifiedEntity[];
  primaryEntity: IdentifiedEntity | null;
  missionName: string;
  missionClassification: 'UNCLASSIFIED' | 'CONFIDENTIAL' | 'SECRET';
  missionPriority: number;
  missionDescription: string;
  missionTags: string[];
  phaseBreakdown: PhaseTokenEstimate[];
  saveAsDefaults: boolean;

  initFromChip: (prompt: string, chipId: string) => void;
  appendStreamLine: (line: Omit<StreamLine, 'id'>) => void;
  addInferredDimension: (dim: Omit<InferredDimension, 'id'>) => void;
  setInferenceProgress: (progress: number) => void;
  completeInference: () => void;
  setAvailableDatasets: (datasets: MissionDataset[]) => void;
  setSelectedDatasetIds: (ids: string[]) => void;
  toggleDataset: (datasetId: string) => void;
  confirmCard: (cardId: SetupCardId) => void;
  unconfirmCard: (cardId: SetupCardId) => void;
  setActiveCard: (cardId: SetupCardId) => void;
  launchMission: () => void;
  resetPlanning: () => void;
  setIdentifiedEntities: (entities: IdentifiedEntity[], primary: IdentifiedEntity) => void;
  updateIdentifiedEntities: (entities: IdentifiedEntity[]) => void;
  setMissionConfig: (config: {
    name: string;
    classification: 'UNCLASSIFIED' | 'CONFIDENTIAL' | 'SECRET';
    priority: number;
    description: string;
    tags: string[];
  }) => void;
  setPhaseBreakdown: (breakdown: PhaseTokenEstimate[]) => void;
  setSaveAsDefaults: (value: boolean) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialPlanningState: {
  pendingPrompt: string;
  pendingChipId: string | null;
  missionType: MissionType | null;
  planningStatus: PlanningStatus;
  streamLines: StreamLine[];
  inferredDimensions: InferredDimension[];
  inferenceProgress: number;
  activeCard: SetupCardId | null;
  confirmedCards: SetupCardId[];
  availableDatasets: MissionDataset[];
  selectedDatasetIds: string[];
  identifiedEntities: IdentifiedEntity[];
  primaryEntity: IdentifiedEntity | null;
  missionName: string;
  missionClassification: 'UNCLASSIFIED' | 'CONFIDENTIAL' | 'SECRET';
  missionPriority: number;
  missionDescription: string;
  missionTags: string[];
  phaseBreakdown: PhaseTokenEstimate[];
  saveAsDefaults: boolean;
} = {
  pendingPrompt: '',
  pendingChipId: null,
  missionType: null,
  planningStatus: 'idle',
  streamLines: [],
  inferredDimensions: [],
  inferenceProgress: 0,
  activeCard: null,
  confirmedCards: [],
  availableDatasets: [],
  selectedDatasetIds: [],
  identifiedEntities: [],
  primaryEntity: null,
  missionName: '',
  missionClassification: 'CONFIDENTIAL',
  missionPriority: 3,
  missionDescription: '',
  missionTags: [],
  phaseBreakdown: [],
  saveAsDefaults: false,
};

// ============================================================================
// Auto-generate tags from mission content (GW-5115)
// ============================================================================

function generateTags(state: {
  missionType: MissionType | null;
  primaryEntity: IdentifiedEntity | null;
  inferredDimensions: InferredDimension[];
}): string[] {
  const tags: string[] = [];

  // Mission type tag
  if (state.missionType && state.missionType in MISSION_TYPE_TAG_SLUGS) {
    tags.push(MISSION_TYPE_TAG_SLUGS[state.missionType]);
  }

  // Target type tag
  if (state.primaryEntity?.type) {
    tags.push(state.primaryEntity.type);
  }

  // Tags from inferred dimensions (lowercased, hyphenated)
  for (const dim of state.inferredDimensions) {
    const slug = dim.label
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
    if (slug && !tags.includes(slug)) {
      tags.push(slug);
    }
  }

  return tags;
}

// ============================================================================
// Auto-generate mission name (GW-5112)
// ============================================================================

function generateMissionName(state: {
  missionType: MissionType | null;
  primaryEntity: IdentifiedEntity | null;
}): string {
  if (!state.missionType) return '';
  const label = getMissionTypeLabel(state.missionType);
  const targetName = state.primaryEntity?.name;
  if (targetName) {
    return `${label} \u2014 ${targetName}`;
  }
  return label;
}

// ============================================================================
// Slice Creator
// ============================================================================

export const createMissionPlanningSlice: StateCreator<
  MissionPlanningSlice,
  [],
  [],
  MissionPlanningSlice
> = (set) => ({
  ...initialPlanningState,

  initFromChip: (prompt, chipId) =>
    set({
      ...initialPlanningState,
      pendingPrompt: prompt,
      pendingChipId: chipId,
      missionType: chipId as MissionType,
      planningStatus: 'inferring',
    }),

  appendStreamLine: (line) =>
    set((state) => {
      // Mark the previous last line as complete
      const updatedLines = state.streamLines.map((l, i) =>
        i === state.streamLines.length - 1 ? { ...l, isComplete: true } : l
      );
      return {
        streamLines: [...updatedLines, { ...line, id: generateId() }],
      };
    }),

  addInferredDimension: (dim) =>
    set((state) => ({
      inferredDimensions: [...state.inferredDimensions, { ...dim, id: generateId() }],
    })),

  setInferenceProgress: (progress) => set({ inferenceProgress: progress }),

  completeInference: () =>
    set((state) => {
      // GW-5112: Auto-populate name if empty
      const autoName = state.missionName === '' ? generateMissionName(state) : state.missionName;

      // GW-5115: Auto-generate tags if none exist
      const autoTags = state.missionTags.length === 0 ? generateTags(state) : state.missionTags;

      return {
        planningStatus: 'confirming',
        streamLines: state.streamLines.map((l) => ({ ...l, isComplete: true })),
        activeCard: 'mission-config',
        confirmedCards: ['mission-type'],
        missionName: autoName,
        missionTags: autoTags,
      };
    }),

  setAvailableDatasets: (datasets) => set({ availableDatasets: datasets }),

  setSelectedDatasetIds: (ids) => set({ selectedDatasetIds: ids }),

  toggleDataset: (datasetId) =>
    set((state) => {
      const isSelected = state.selectedDatasetIds.includes(datasetId);
      return {
        selectedDatasetIds: isSelected
          ? state.selectedDatasetIds.filter((id) => id !== datasetId)
          : [...state.selectedDatasetIds, datasetId],
      };
    }),

  confirmCard: (cardId) =>
    set((state) => {
      const alreadyConfirmed = state.confirmedCards.includes(cardId);
      const confirmedCards = alreadyConfirmed
        ? state.confirmedCards
        : [...state.confirmedCards, cardId];

      // Advance activeCard to the next unconfigured card
      const nextCard =
        CARD_ORDER.find((id) => !confirmedCards.includes(id) && id !== cardId) ?? null;

      return { confirmedCards, activeCard: nextCard };
    }),

  unconfirmCard: (cardId) =>
    set((state) => ({
      confirmedCards: state.confirmedCards.filter((id) => id !== cardId),
      activeCard: cardId,
    })),

  setActiveCard: (cardId) => set({ activeCard: cardId }),

  launchMission: () => set({ planningStatus: 'launching' }),

  resetPlanning: () => set(initialPlanningState),

  setIdentifiedEntities: (entities, primary) =>
    set({ identifiedEntities: entities, primaryEntity: primary }),

  updateIdentifiedEntities: (entities) =>
    set((state) => ({
      identifiedEntities: entities,
      primaryEntity: entities.length > 0 ? entities[0] : state.primaryEntity,
    })),

  setMissionConfig: (config) =>
    set({
      missionName: config.name,
      missionClassification: config.classification,
      missionPriority: config.priority,
      missionDescription: config.description,
      missionTags: config.tags,
    }),

  setPhaseBreakdown: (breakdown) => set({ phaseBreakdown: breakdown }),

  setSaveAsDefaults: (value) => set({ saveAsDefaults: value }),
});
